import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Database, Search, Edit, Trash2, Download, AlertTriangle, Save, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';

const AdminMongoDB = () => {
  const [activeCollection, setActiveCollection] = useState('contacts'); // contacts, appointments, devis_leads
  const [documents, setDocuments] = useState([]);
  const [summary, setSummary] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  // Edit modal
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editOrigineMarketing, setEditOrigineMarketing] = useState('');
  const [editMotifPerte, setEditMotifPerte] = useState('');
  const [editAdminNotes, setEditAdminNotes] = useState('');
  
  // Delete confirmation
  const [docToDelete, setDocToDelete] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const LIMIT = 25;

  const motifPerteOptions = [
    { value: 'aucun', label: '-- Aucun --' },
    { value: 'Trop cher', label: 'Trop cher' },
    { value: 'Délai trop long', label: 'Délai trop long' },
    { value: 'Choisi autre prestataire', label: 'Choisi autre prestataire' },
    { value: 'Pas de réponse', label: 'Pas de réponse' },
    { value: 'Projet annulé', label: 'Projet annulé' },
    { value: 'Autre', label: 'Autre' }
  ];

  // Fetch summary stats for collection
  const fetchSummary = async (collection) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/mongo/${collection}/summary`);
      setSummary(response.data);
    } catch (error) {
      console.error('Error fetching summary:', error);
    }
  };

  // Fetch documents for collection
  const fetchDocuments = async (collection, pageNum = 1, search = '') => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/admin/mongo/${collection}/list`, {
        params: {
          page: pageNum,
          limit: LIMIT,
          search: search || undefined
        }
      });
      setDocuments(response.data.documents);
      setTotalPages(response.data.total_pages);
      setPage(pageNum);
    } catch (error) {
      console.error('Error fetching documents:', error);
      alert('Erreur lors du chargement des données');
    } finally {
      setIsLoading(false);
    }
  };

  // Initial load and when collection changes
  useEffect(() => {
    fetchSummary(activeCollection);
    fetchDocuments(activeCollection, 1, searchTerm);
  }, [activeCollection, searchTerm]);

  // Handle search
  const handleSearch = () => {
    setPage(1);
    fetchDocuments(activeCollection, 1, searchTerm);
  };

  // Open edit modal
  const openEditModal = (doc) => {
    setSelectedDoc(doc);
    setEditOrigineMarketing(doc.origine_marketing || '');
    setEditMotifPerte(doc.motif_perte || 'aucun');
    setEditAdminNotes(doc.admin_notes || '');
    setIsEditModalOpen(true);
  };

  // Save edits
  const handleSaveEdit = async () => {
    try {
      const updates = {
        origine_marketing: editOrigineMarketing,
        motif_perte: editMotifPerte === 'aucun' ? '' : editMotifPerte,
        admin_notes: editAdminNotes
      };

      await axios.put(
        `${BACKEND_URL}/api/admin/mongo/${activeCollection}/${selectedDoc._id}`,
        updates
      );

      alert('✅ Document mis à jour avec succès');
      setIsEditModalOpen(false);
      fetchDocuments(activeCollection, page, searchTerm);
      fetchSummary(activeCollection);
    } catch (error) {
      console.error('Error updating document:', error);
      alert(`❌ Erreur: ${error.response?.data?.detail || error.message}`);
    }
  };

  // Open delete confirmation
  const openDeleteModal = (doc) => {
    setDocToDelete(doc);
    setIsDeleteModalOpen(true);
  };

  // Confirm delete
  const handleConfirmDelete = async () => {
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/mongo/${activeCollection}/${docToDelete._id}`);
      
      alert('✅ Document supprimé avec succès');
      setIsDeleteModalOpen(false);
      setDocToDelete(null);
      fetchDocuments(activeCollection, page, searchTerm);
      fetchSummary(activeCollection);
    } catch (error) {
      console.error('Error deleting document:', error);
      alert(`❌ Erreur: ${error.response?.data?.detail || error.message}`);
    }
  };

  // Export CSV
  const handleExportCSV = async () => {
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/mongo/${activeCollection}/export`,
        { search: searchTerm }
      );

      // Create blob and download
      const blob = new Blob([response.data.csv_data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${activeCollection}_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
      
      alert(`✅ ${response.data.total_rows} lignes exportées`);
    } catch (error) {
      console.error('Error exporting CSV:', error);
      alert('❌ Erreur lors de l\'export CSV');
    }
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  // Check if document can be deleted
  const canDelete = (doc) => {
    const status = doc.status?.toLowerCase() || '';
    const adminStatus = doc.admin_status?.toLowerCase() || '';
    const source = doc.source?.toLowerCase() || '';
    
    const isPerdu = status === 'lost' || status === 'perdu' || adminStatus === 'perdu' || adminStatus === 'lost';
    const isTest = source === 'test';
    
    return isPerdu || isTest;
  };

  // Render table columns based on collection
  const renderTableHeader = () => {
    const commonHeaders = ['Nom', 'Prénom', 'Email', 'Téléphone'];
    
    if (activeCollection === 'contacts') {
      return [...commonHeaders, 'Status', 'Source', 'Ville', 'Surface (m²)', 'Budget Estimé', 'Motif Perte', 'Créé le', 'Actions'];
    } else if (activeCollection === 'appointments') {
      return [...commonHeaders, 'Service', 'Date/Heure', 'Status', 'Source', 'Ville', 'Créé le', 'Actions'];
    } else if (activeCollection === 'devis_leads') {
      return [...commonHeaders, 'Projet', 'Admin Status', 'Status', 'Source', 'Ville', 'Surface (m²)', 'Budget Estimé', 'Créé le', 'Actions'];
    }
    return commonHeaders;
  };

  const renderTableRow = (doc) => {
    const commonCells = [
      <td key="nom" className="py-3 px-4 text-slate-300">{doc.nom || 'N/A'}</td>,
      <td key="prenom" className="py-3 px-4 text-slate-300">{doc.prenom || 'N/A'}</td>,
      <td key="email" className="py-3 px-4 text-slate-300">{doc.email || 'N/A'}</td>,
      <td key="telephone" className="py-3 px-4 text-slate-300">{doc.telephone || 'N/A'}</td>
    ];

    let specificCells = [];

    if (activeCollection === 'contacts') {
      specificCells = [
        <td key="status" className="py-3 px-4 text-slate-300">{doc.status || 'N/A'}</td>,
        <td key="source" className="py-3 px-4 text-slate-300">{doc.source || 'N/A'}</td>,
        <td key="ville" className="py-3 px-4 text-slate-300">{doc.ville || 'N/A'}</td>,
        <td key="surface" className="py-3 px-4 text-slate-300">{doc.surface_estimee_m2 || 'N/A'}</td>,
        <td key="budget" className="py-3 px-4 text-slate-300">{doc.budget_estime || 'N/A'}</td>,
        <td key="motif" className="py-3 px-4 text-slate-300">{doc.motif_perte || 'N/A'}</td>
      ];
    } else if (activeCollection === 'appointments') {
      specificCells = [
        <td key="service" className="py-3 px-4 text-slate-300">{doc.service || 'N/A'}</td>,
        <td key="datetime" className="py-3 px-4 text-slate-300">{doc.date} {doc.time || ''}</td>,
        <td key="status" className="py-3 px-4 text-slate-300">{doc.status || 'N/A'}</td>,
        <td key="source" className="py-3 px-4 text-slate-300">{doc.source || 'N/A'}</td>,
        <td key="ville" className="py-3 px-4 text-slate-300">{doc.ville || 'N/A'}</td>
      ];
    } else if (activeCollection === 'devis_leads') {
      specificCells = [
        <td key="projet" className="py-3 px-4 text-slate-300">{doc.type_beton || 'N/A'} ({doc.surface || 0} m²)</td>,
        <td key="admin_status" className="py-3 px-4 text-slate-300">{doc.admin_status || 'N/A'}</td>,
        <td key="status" className="py-3 px-4 text-slate-300">{doc.status || 'N/A'}</td>,
        <td key="source" className="py-3 px-4 text-slate-300">{doc.source || 'N/A'}</td>,
        <td key="ville" className="py-3 px-4 text-slate-300">{doc.ville || 'N/A'}</td>,
        <td key="surface" className="py-3 px-4 text-slate-300">{doc.surface_estimee_m2 || doc.surface || 'N/A'}</td>,
        <td key="budget" className="py-3 px-4 text-slate-300">{doc.budget_estime || 'N/A'}</td>
      ];
    }

    return (
      <tr key={doc._id} className="border-b border-slate-700 hover:bg-slate-800/50">
        {commonCells}
        {specificCells}
        <td className="py-3 px-4 text-slate-300">{formatDate(doc.created_at)}</td>
        <td className="py-3 px-4">
          <div className="flex gap-2">
            <button
              onClick={() => openEditModal(doc)}
              className="p-1 text-blue-400 hover:text-blue-300 hover:bg-slate-700 rounded"
              title="Éditer"
            >
              <Edit className="w-4 h-4" />
            </button>
            {canDelete(doc) ? (
              <button
                onClick={() => openDeleteModal(doc)}
                className="p-1 text-red-400 hover:text-red-300 hover:bg-slate-700 rounded"
                title="Supprimer"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            ) : (
              <button
                className="p-1 text-slate-600 cursor-not-allowed"
                title="Suppression non autorisée"
                disabled
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-slate-100">
      {/* Header */}
      <div className="bg-slate-800 border-b border-slate-700 py-6 px-8">
        <div className="max-w-7xl mx-auto">
          <Link 
            to="/admin/dashboard" 
            className="inline-flex items-center text-slate-400 hover:text-slate-200 mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour au Dashboard
          </Link>
          <div className="flex items-center gap-3">
            <Database className="w-8 h-8 text-green-400" />
            <h1 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-teal-400">
              Données CRM (MongoDB)
            </h1>
          </div>
          <p className="text-slate-400 mt-2">
            Gestion technique des collections MongoDB - CRUD limité et audit
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-8 py-8">
        {/* Summary Stats */}
        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm text-slate-400">Total Documents</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-green-400">{summary.total}</div>
              </CardContent>
            </Card>
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm text-slate-400">🎉 Gagné</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-green-400">{summary.gagne}</div>
              </CardContent>
            </Card>
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm text-slate-400">❌ Perdu</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-red-400">{summary.perdu}</div>
              </CardContent>
            </Card>
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm text-slate-400">Période</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-xs text-slate-400">
                  Plus ancien: {formatDate(summary.oldest_created_at)}<br/>
                  Plus récent: {formatDate(summary.newest_created_at)}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Collection Tabs */}
        <div className="flex gap-4 mb-6">
          <button
            onClick={() => setActiveCollection('contacts')}
            className={`px-4 py-2 rounded-md font-medium transition-colors ${
              activeCollection === 'contacts'
                ? 'bg-green-600 text-white'
                : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
            }`}
          >
            Contacts DB
          </button>
          <button
            onClick={() => setActiveCollection('appointments')}
            className={`px-4 py-2 rounded-md font-medium transition-colors ${
              activeCollection === 'appointments'
                ? 'bg-green-600 text-white'
                : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
            }`}
          >
            Rendez-vous DB
          </button>
          <button
            onClick={() => setActiveCollection('devis_leads')}
            className={`px-4 py-2 rounded-md font-medium transition-colors ${
              activeCollection === 'devis_leads'
                ? 'bg-green-600 text-white'
                : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
            }`}
          >
            Leads Calculator DB
          </button>
        </div>

        {/* Search & Actions */}
        <div className="flex gap-4 mb-6">
          <div className="flex-1 flex gap-2">
            <Input
              type="text"
              placeholder="Rechercher par nom, email, téléphone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              className="bg-slate-800 border-slate-600 text-slate-200"
            />
            <Button
              onClick={handleSearch}
              className="bg-green-600 hover:bg-green-700"
            >
              <Search className="w-4 h-4 mr-2" />
              Rechercher
            </Button>
          </div>
          <Button
            onClick={handleExportCSV}
            variant="outline"
            className="border-slate-600 text-slate-300 hover:bg-slate-700"
          >
            <Download className="w-4 h-4 mr-2" />
            Exporter CSV
          </Button>
        </div>

        {/* Table */}
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-900 border-b border-slate-700">
                  <tr>
                    {renderTableHeader().map((header, idx) => (
                      <th key={idx} className="py-3 px-4 text-left text-sm font-semibold text-slate-300">
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td colSpan="20" className="py-8 text-center text-slate-400">
                        Chargement...
                      </td>
                    </tr>
                  ) : documents.length === 0 ? (
                    <tr>
                      <td colSpan="20" className="py-8 text-center text-slate-400">
                        Aucun document trouvé
                      </td>
                    </tr>
                  ) : (
                    documents.map(doc => renderTableRow(doc))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center gap-2 mt-6">
            <Button
              onClick={() => fetchDocuments(activeCollection, page - 1, searchTerm)}
              disabled={page === 1}
              variant="outline"
              className="border-slate-600 text-slate-300"
            >
              Précédent
            </Button>
            <span className="px-4 py-2 text-slate-300">
              Page {page} sur {totalPages}
            </span>
            <Button
              onClick={() => fetchDocuments(activeCollection, page + 1, searchTerm)}
              disabled={page === totalPages}
              variant="outline"
              className="border-slate-600 text-slate-300"
            >
              Suivant
            </Button>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 text-slate-200 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-green-400">
              Éditer Document (Limité)
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Modification limitée aux champs techniques uniquement
            </DialogDescription>
          </DialogHeader>
          
          {selectedDoc && (
            <div className="space-y-4 py-4">
              {/* Read-only info */}
              <div className="bg-slate-900 p-4 rounded-md border border-slate-700">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-slate-500">Nom:</span> <span className="text-slate-300">{selectedDoc.nom} {selectedDoc.prenom}</span></div>
                  <div><span className="text-slate-500">Email:</span> <span className="text-slate-300">{selectedDoc.email}</span></div>
                  <div><span className="text-slate-500">Téléphone:</span> <span className="text-slate-300">{selectedDoc.telephone}</span></div>
                  <div><span className="text-slate-500">Status:</span> <span className="text-slate-300">{selectedDoc.status || selectedDoc.admin_status}</span></div>
                </div>
              </div>

              {/* Editable fields */}
              <div className="space-y-4">
                <div>
                  <Label className="text-slate-300 mb-2 block">Origine Marketing</Label>
                  <Input
                    type="text"
                    value={editOrigineMarketing}
                    onChange={(e) => setEditOrigineMarketing(e.target.value)}
                    placeholder="Ex: Google Ads, Facebook, Bouche-à-oreille..."
                    className="bg-slate-900 border-slate-600 text-slate-200"
                  />
                </div>

                <div>
                  <Label className="text-slate-300 mb-2 block">Motif Perte</Label>
                  <Select value={editMotifPerte} onValueChange={setEditMotifPerte}>
                    <SelectTrigger className="bg-slate-900 border-slate-600 text-slate-200">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {motifPerteOptions.map(option => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-slate-300 mb-2 block">Notes Admin (techniques)</Label>
                  <Textarea
                    value={editAdminNotes}
                    onChange={(e) => setEditAdminNotes(e.target.value)}
                    placeholder="Notes internes techniques..."
                    rows={4}
                    className="bg-slate-900 border-slate-600 text-slate-200"
                  />
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setIsEditModalOpen(false)}
              className="border-slate-600 text-slate-300"
            >
              <X className="w-4 h-4 mr-2" />
              Annuler
            </Button>
            <Button
              onClick={handleSaveEdit}
              className="bg-green-600 hover:bg-green-700"
            >
              <Save className="w-4 h-4 mr-2" />
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 text-slate-200">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-red-400 flex items-center gap-2">
              <AlertTriangle className="w-6 h-6" />
              Confirmer la Suppression
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Cette action est irréversible et sera enregistrée dans l&apos;audit log.
            </DialogDescription>
          </DialogHeader>
          
          {docToDelete && (
            <div className="py-4">
              <div className="bg-slate-900 p-4 rounded-md border border-red-900/50">
                <p className="text-slate-300 mb-2">Êtes-vous sûr de vouloir supprimer ce document ?</p>
                <div className="text-sm space-y-1">
                  <div><span className="text-slate-500">Nom:</span> <span className="font-semibold text-red-400">{docToDelete.nom} {docToDelete.prenom}</span></div>
                  <div><span className="text-slate-500">Email:</span> <span className="font-semibold text-red-400">{docToDelete.email}</span></div>
                  <div><span className="text-slate-500">Status:</span> <span className="text-slate-300">{docToDelete.status || docToDelete.admin_status}</span></div>
                  <div><span className="text-slate-500">Source:</span> <span className="text-slate-300">{docToDelete.source}</span></div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setIsDeleteModalOpen(false);
                setDocToDelete(null);
              }}
              className="border-slate-600 text-slate-300"
            >
              Annuler
            </Button>
            <Button
              onClick={handleConfirmDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Confirmer la Suppression
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminMongoDB;
